import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Dialog, DialogContent, DialogActions } from '@mui/material';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import { useParams, useNavigate } from 'react-router-dom';
import { useDeleteJobCardMutation } from '../api/apiSlice';

const ERROR_COLOR = '#ef4444';

const SignalBanner = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { job_card_reference_id } = useParams();
  const navigate = useNavigate();
  const [deleteJobCard, { isSuccess, isLoading }] = useDeleteJobCardMutation();

  const canAbort = Boolean(job_card_reference_id);

  useEffect(() => {
    if (isSuccess) {
      setConfirmOpen(false);
      navigate('/installer/');
    }
  }, [isSuccess, navigate]);

  return (
    <>
      <Box sx={{
        mx: 2,
        mt: 1,
        mb: 0.5,
        borderRadius: '10px',
        border: `1px solid ${ERROR_COLOR}55`,
        backgroundColor: `${ERROR_COLOR}0f`,
        p: '12px 14px 10px',
      }}>
        {/* Title row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '7px' }}>
          <SignalWifiOffIcon sx={{ color: ERROR_COLOR, fontSize: '1.1rem', flexShrink: 0 }} />
          <Typography sx={{ color: ERROR_COLOR, fontWeight: 700, fontSize: '0.88rem' }}>
            Insufficient signal
          </Typography>
        </Box>

        {/* Body */}
        <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.80rem', lineHeight: 1.55 }}>
          You cannot proceed until signal is re-established. If you can't recover it on site, do not start a new job — mark the location and report from the dashboard.
        </Typography>

        {/* Abort link */}
        {canAbort && (
          <Typography
            component="button"
            onClick={() => setConfirmOpen(true)}
            sx={{
              mt: '10px',
              color: ERROR_COLOR,
              fontSize: '0.80rem',
              fontWeight: 600,
              textDecoration: 'underline',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              p: 0,
              display: 'block',
              fontFamily: 'inherit',
              '&:hover': { opacity: 0.8 },
            }}
          >
            Abort this installation
          </Typography>
        )}
      </Box>

      {/* Confirm dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => !isLoading && setConfirmOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#161B22',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '16px',
            mx: 2,
          },
        }}
      >
        <DialogContent sx={{ p: '24px 24px 16px' }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', mb: '8px' }}>
            Abort this installation?
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', lineHeight: 1.5 }}>
            This cannot be undone. The job card will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={isLoading}
            fullWidth
            sx={{
              backgroundColor: 'rgba(255,255,255,0.06)',
              color: '#fff',
              borderRadius: '10px',
              fontWeight: 700,
              py: 1.2,
              textTransform: 'none',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
            }}
          >
            Back
          </Button>
          <Button
            onClick={() => deleteJobCard({ body: { reference_id: job_card_reference_id } })}
            disabled={isLoading}
            fullWidth
            sx={{
              backgroundColor: '#ef4444',
              color: '#fff',
              borderRadius: '10px',
              fontWeight: 700,
              py: 1.2,
              textTransform: 'none',
              '&:hover': { backgroundColor: '#dc2626' },
              '&.Mui-disabled': { opacity: 0.6 },
            }}
          >
            Confirm Abort
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SignalBanner;
